/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, ReferenceLine,
} from 'recharts';

// ── Colours ───────────────────────────────────────────────────────────────────
const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', yellowDark:'#E4AC49',
  green:'#7EA594', greenLight:'#EAF2EF',
  cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  amber:'#E07B00', amberLight:'#FFF3E0',
};

const P = {
  teal:          '#4A7B8C',
  pastelGreen:   '#A8D5B5',
  pastelBlue:    '#BCD9E5',
  pastelAmber:   '#F0C87A',
  revenueAccent: '#445366',
  ebitdaAccent:  '#7EA594',
  payrollAccent: '#7C3AED',
  expensesAccent:'#E07B00',
  foodAccent:    '#E07B00',
  bevAccent:     '#3A8FA3',
};

const VENUE_META = {
  'Box Jordaan':  { color: '#445366', short: 'Jordaan',  lmKey: 'jordaan'  },
  'Box Plantage': { color: '#3A8FA3', short: 'Plantage', lmKey: 'plantage' },
  'Sassys':       { color: '#7EA594', short: 'Sassys',   lmKey: 'sassys'   },
};
const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();

// ── Helpers ───────────────────────────────────────────────────────────────────
const safe   = (n) => isNaN(n) || n == null ? 0 : Number(n);
const fmt    = (n) => n == null || isNaN(n) || n === 0 ? '—'
  : `€${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtK   = (v) => `€${(v / 1000).toFixed(0)}k`;
const fmtK2  = (n) => { if (!n || isNaN(n)) return '—'; return `€${Math.round(n / 1000)}k`; };
const pctFmt = (n) => n == null || isNaN(n) ? '—' : `${n.toFixed(1)}%`;
const pctTip = (v) => v != null ? `${v}%` : '—';

function useIsMobile() {
  const [mob, setMob] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function weekToMonth(week, year) {
  return new Date(year, 0, 1 + (week - 1) * 7).getMonth() + 1;
}

// Sum daily revenue for a venue/year/month from results_daily rows
function dailyMonthRev(dailyRows, monthNum) {
  return (dailyRows || [])
    .filter(d => new Date(d.date).getMonth() + 1 === monthNum && (d.total_revenue||0) > 0)
    .reduce((s, d) => s + (d.total_revenue||0), 0);
}

// Most recent day with revenue in a daily array
function latestDayOfMonth(dailyRows, monthNum) {
  const rows = (dailyRows || []).filter(d => new Date(d.date).getMonth() + 1 === monthNum && (d.total_revenue||0) > 0);
  if (!rows.length) return null;
  return new Date(rows.map(d => d.date).sort().reverse()[0]).getDate();
}

function rowEBITDA(r) {
  if (!r) return 0;
  return safe(r.actual_revenue)
    - safe(r.food_cost) - safe(r.bev_cost)
    - safe(r.oedipus_rent) - safe(r.oedipus_utilities) - safe(r.oedipus_rev_share)
    - safe(r.kitchen_payroll) - safe(r.foh_payroll)
    - safe(r.lm_prep_payroll_alloc) - safe(r.lm_transfers_alloc)
    - safe(r.group_costs) - safe(r.other_opex);
}

function rowPayroll(r) {
  if (!r) return 0;
  return safe(r.kitchen_payroll) + safe(r.foh_payroll) + safe(r.lm_prep_payroll_alloc);
}

// Expenses = Revenue - Payroll - EBITDA  (guaranteed to reconcile)
function rowExpenses(r) {
  if (!r) return 0;
  return safe(r.actual_revenue) - rowPayroll(r) - rowEBITDA(r);
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function SectionLabel({ text }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: C.mid,
      textTransform: 'uppercase', letterSpacing: '0.07em',
      borderBottom: `1px solid ${C.border}`,
      paddingBottom: 5, marginBottom: 12, marginTop: 22,
    }}>
      {text}
    </div>
  );
}

function ChartLegend({ items }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
      {items.map(({ color, label, dashed }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.mid }}>
          {dashed
            ? <span style={{ width: 16, height: 2, borderTop: `2px dashed ${color}`, display: 'inline-block' }}/>
            : <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }}/>
          }
          {label}
        </div>
      ))}
    </div>
  );
}

function DarkTooltip({ active, payload, label, fmtVal }) {
  if (!active || !payload?.length) return null;
  const fv = fmtVal || ((v) => v);
  return (
    <div style={{
      background: C.charcoal, borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      fontSize: 12, minWidth: 140,
    }}>
      <div style={{ color: C.skyBlue, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map(p => p.value != null && (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill, display: 'inline-block', flexShrink: 0 }}/>
          <span style={{ color: '#aaa' }}>{p.name}:</span>
          <span style={{ color: C.white, fontWeight: 700 }}>{fv(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const pillStyle = (active) => ({
  padding: '5px 14px', borderRadius: 20,
  border: `1px solid ${active ? C.navy : C.border}`,
  cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
  background: active ? C.navy : C.white,
  color: active ? C.white : C.mid, transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

// ── Controls bar ──────────────────────────────────────────────────────────────
function ControlsBar({ tab, setTab, month, year, onPrev, onNext, disableNext, setYear, showYTD, setShowYTD, isMobile }) {
  const tabs = ['venues', 'group', 'costs'];
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 8 : 10,
      alignItems: isMobile ? 'stretch' : 'center',
      marginBottom: 20,
    }}>
      {/* Left: tab switcher + year pills */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: isMobile ? 'space-between' : undefined }}>
        <div style={{ display: 'flex', gap: 2, background: C.cream, borderRadius: 22, padding: '3px 4px', border: `1px solid ${C.border}` }}>
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '5px 12px', borderRadius: 18, border: 'none', cursor: 'pointer', fontSize: 12,
              fontWeight: tab === t ? 700 : 400,
              background: tab === t ? C.navy : 'transparent',
              color: tab === t ? C.white : C.mid, transition: 'all 0.15s',
            }}>
              {t === 'venues' ? 'Venues' : t === 'group' ? 'Group' : 'Costs'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          {[CURRENT_YEAR, CURRENT_YEAR - 1].map(y => (
            <button key={y} onClick={() => setYear(y)} style={pillStyle(year === y)}>{y}</button>
          ))}
        </div>
      </div>

      {/* Right: month stepper + YTD toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        justifyContent: isMobile ? 'space-between' : undefined,
        marginLeft: isMobile ? 0 : 'auto',
      }}>
        {/* Month stepper */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={onPrev} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 16, color: C.mid, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 800, color: showYTD ? C.mid : C.charcoal, minWidth: 80, textAlign: 'center', transition: 'color 0.15s' }}>
            {showYTD ? `Jan–${MONTHS[month - 1]}` : `${MONTHS[month - 1]} ${year}`}
          </span>
          <button onClick={onNext} disabled={disableNext} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: disableNext ? 'not-allowed' : 'pointer', fontSize: 16, color: disableNext ? C.border : C.mid, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
        </div>
        {/* YTD toggle */}
        <button onClick={() => setShowYTD(v => !v)} style={{
          padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 700,
          background: showYTD ? C.navy : C.cream,
          color: showYTD ? C.white : C.mid,
          border: `1px solid ${showYTD ? C.navy : C.border}`,
          transition: 'all 0.15s',
        }}>
          YTD
        </button>
      </div>
    </div>
  );
}


// ── Venue Modal ───────────────────────────────────────────────────────────────
function VenueModal({ venue, month, year, monthly, monthlyLY, weekly, weeklyLY, dailyRev, dailyRevLY, showYTD, onClose }) {
  const isMobile = useIsMobile();
  const meta     = VENUE_META[venue];
  const isSassys = venue === 'Sassys';

  const mRow   = monthly.find(r => r.month === month);
  const mRowLY = monthlyLY.find(r => r.month === month);
  const actual      = dailyMonthRev(dailyRev, month) || safe(mRow?.actual_revenue);
  const ly          = dailyMonthRev(dailyRevLY, month) || safe(mRowLY?.actual_revenue);
  const ebitda      = rowEBITDA(mRow);
  const ebitdaLY    = rowEBITDA(mRowLY);
  const ebitdaMar   = actual > 0 ? (ebitda / actual) * 100 : 0;
  const ebitdaMarLY = ly > 0 ? (ebitdaLY / ly) * 100 : 0;

  const weeksInMonth = (weekly || [])
    .filter(r => weekToMonth(r.week, year) === month)
    .sort((a, b) => a.week - b.week)
    .map(r => {
      // Use daily sum for week if available
      const dailySum = (dailyRev || [])
        .filter(d => {
          const dt = new Date(d.date);
          const wk = 1 + Math.round(((new Date(dt.getFullYear(),0,4+Math.round((dt-new Date(dt.getFullYear(),0,4))/864e5)).setHours(0,0,0,0) - new Date(dt.getFullYear(),0,4)) / 864e5 - 3 + (new Date(dt.getFullYear(),0,4).getDay()+6)%7) / 7);
          return wk === r.week && (d.total_revenue||0) > 0;
        })
        .reduce((s,d) => s+(d.total_revenue||0), 0);
      const total_revenue = dailySum > 0 ? dailySum : safe(r.brunch_revenue) + safe(r.dinner_revenue);
      return { ...r, total_revenue };
    });

  const weeksInMonthLY = (weeklyLY || [])
    .filter(r => weekToMonth(r.week, year - 1) === month)
    .sort((a, b) => a.week - b.week);

  const allWeeks = [...new Set([
    ...(weekly || []).map(r => r.week),
    ...(weeklyLY || []).map(r => r.week),
  ])].sort((a, b) => a - b);

  const trendData = allWeeks.map(w => {
    const cy = (weekly || []).find(r => r.week === w);
    const ly = (weeklyLY || []).find(r => r.week === w);
    const cyW = weeksInMonth.find(r => r.week === w);
    const cyRev = cyW?.total_revenue ?? (cy ? safe(cy.brunch_revenue) + safe(cy.dinner_revenue) : null);
    const lyRev = ly ? safe(ly.brunch_revenue) + safe(ly.dinner_revenue) : null;
    return { week: `W${w}`, Revenue: cyRev, 'LY': lyRev };
  });

  const fyData = MONTHS.map((label, i) => {
    const m   = i + 1;
    const cy  = monthly.find(r => r.month === m);
    const lyr = monthlyLY.find(r => r.month === m);
    const cyRev     = dailyMonthRev(dailyRev, m) || safe(cy?.actual_revenue);
    const lyRev     = dailyMonthRev(dailyRevLY, m) || safe(lyr?.actual_revenue);
    const bgtRev    = safe(cy?.budget_revenue);
    const cyFoodRev = safe(cy?.food_revenue) || cyRev;
    const lyFoodRev = safe(lyr?.food_revenue) || lyRev;
    const cyBevRev  = safe(cy?.bev_revenue);
    const toP = (cost, rev) => rev > 0 ? parseFloat(((cost / rev) * 100).toFixed(1)) : null;
    const cyPay  = rowPayroll(cy);
    const lyPay  = rowPayroll(lyr);
    const bgtPay = safe(cy?.budget_kitchen_payroll) + safe(cy?.budget_foh_payroll);
    return {
      label,
      payrollPct: toP(cyPay, cyRev), payrollLY: toP(lyPay, lyRev), payrollBgt: toP(bgtPay, bgtRev),
      foodPct:    toP(safe(cy?.food_cost), cyFoodRev), foodLY: toP(safe(lyr?.food_cost), lyFoodRev), foodBgt: toP(safe(cy?.budget_food_cost), bgtRev),
      bevPct:     toP(safe(cy?.bev_cost), cyBevRev), bevLY: toP(safe(lyr?.bev_cost), safe(lyr?.bev_revenue)), bevBgt: toP(safe(cy?.budget_bev_cost), cyBevRev),
    };
  });

  const ebitdaFY = MONTHS.map((label, i) => {
    const m   = i + 1;
    const cy  = monthly.find(r => r.month === m);
    const lyr = monthlyLY.find(r => r.month === m);
    return { label, EBITDA: cy ? rowEBITDA(cy) : null, 'LY EBITDA': lyr ? rowEBITDA(lyr) : null };
  });

  function ModalKPI({ label, value, lyValue, isPercent }) {
    const delta    = lyValue != null ? value - lyValue : null;
    const hasDelta = delta != null;
    const good     = hasDelta ? delta >= 0 : null;
    const bg       = good === true ? C.greenLight : good === false ? C.redLight : C.cream;
    const border   = good === true ? `${C.green}40` : good === false ? `${C.red}30` : C.border;
    const numColor = good === true ? C.green : good === false ? C.red : C.charcoal;
    const prefix   = hasDelta && delta > 0 ? '+' : '';
    const dFmt     = isPercent ? `${prefix}${delta?.toFixed(1)}pp vs LY` : `${prefix}${fmt(Math.round(delta))} vs LY`;
    return (
      <div style={{ background: bg, borderRadius: 10, border: `1px solid ${border}`, padding: '12px 10px', flex: 1 }}>
        <div style={{ fontSize: 10, color: C.mid, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4, letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: numColor, marginBottom: hasDelta ? 4 : 0 }}>
          {isPercent ? pctFmt(value) : fmt(value)}
        </div>
        {hasDelta && <div style={{ fontSize: 10, fontWeight: 700, color: numColor }}>{dFmt}</div>}
      </div>
    );
  }

  const box = { background: C.cream, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(47,58,66,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.white, borderRadius: isMobile ? '20px 20px 0 0' : 16, width: '100%', maxWidth: 700, maxHeight: isMobile ? '94vh' : '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <div style={{ width: 36, height: 4, background: C.border, borderRadius: 2 }}/>
          </div>
        )}
        <div style={{ padding: isMobile ? '14px 18px' : '18px 24px', background: C.navy, borderRadius: isMobile ? 0 : '16px 16px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, flexShrink: 0 }}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: isMobile ? 16 : 18, color: C.white }}>{meta.short}</div>
            <div style={{ fontSize: 12, color: C.skyBlue, marginTop: 2 }}>
              {showYTD ? `Jan–${MONTHS[month - 1]} ${year} YTD` : `${MONTHS[month - 1]} ${year}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, cursor: 'pointer', color: C.white, padding: '6px 10px', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: isMobile ? '16px 18px 32px' : '20px 24px 36px', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 4 }}>
            <ModalKPI label="Revenue"       value={actual}    lyValue={ly > 0 ? ly : null}                    isPercent={false}/>
            <ModalKPI label="EBITDA"        value={ebitda}    lyValue={ebitdaLY !== 0 ? ebitdaLY : null}       isPercent={false}/>
            <ModalKPI label="EBITDA Margin" value={ebitdaMar} lyValue={ebitdaMarLY !== 0 ? ebitdaMarLY : null} isPercent={true}/>
          </div>

          {weeksInMonth.length > 0 && (
            <>
              <SectionLabel text="Weekly Breakdown"/>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                      {['Week','Revenue','Hours','Rev/Hr','vs LY'].map((h, i) => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: i === 0 ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeksInMonth.map((r, idx) => {
                      const lyRow = weeksInMonthLY.find(x => x.week === r.week);
                      const lyRev = lyRow ? safe(lyRow.brunch_revenue) + safe(lyRow.dinner_revenue) : null; // LY weekly uses results table (no daily for LY weeks in modal)
                      const diff  = lyRev ? r.total_revenue - lyRev : null;
                      const revHr = r.total_hours > 0 ? Math.round(r.total_revenue / r.total_hours) : null;
                      const good  = diff != null && diff >= 0;
                      return (
                        <tr key={r.week} style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? C.white : C.cream }}>
                          <td style={{ padding: '9px 12px', fontWeight: 700, color: C.charcoal }}>W{r.week}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: C.charcoal }}>{fmt(r.total_revenue)}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', color: C.mid }}>{r.total_hours != null ? Math.round(r.total_hours) : '—'}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', color: C.charcoal, fontWeight: 600 }}>{revHr ? fmt(revHr) : '—'}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                            {diff != null ? <span style={{ fontWeight: 700, color: good ? C.green : C.red, fontSize: 11 }}>{good ? '+' : ''}{fmt(Math.round(diff))}</span> : <span style={{ color: C.border }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {trendData.some(d => d.Revenue) && (
            <>
              <SectionLabel text="Revenue Trend — Full Year"/>
              <div style={box}>
                <ChartLegend items={[{ color: meta.color, label: String(year) }, { color: C.mid, label: String(year - 1), dashed: true }]}/>
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="week" tick={{ fontSize: 9, fill: C.mid }} axisLine={false} tickLine={false} interval={3}/>
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                    <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
                    <Line type="monotone" dataKey="Revenue" name={String(year)}     stroke={meta.color} strokeWidth={2}   dot={false} connectNulls/>
                    <Line type="monotone" dataKey="LY"      name={String(year - 1)} stroke={C.mid}      strokeWidth={1.5} dot={false} strokeDasharray="4 2" connectNulls/>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          <SectionLabel text="EBITDA — Full Year vs LY"/>
          <div style={box}>
            <ChartLegend items={[{ color: P.pastelGreen, label: String(year) }, { color: P.teal, label: String(year - 1) }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ebitdaFY} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={3} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
                <ReferenceLine y={0} stroke={C.mid} strokeOpacity={0.4}/>
                <Bar dataKey="EBITDA"    name={String(year)}     fill={P.pastelGreen} radius={[3,3,0,0]}/>
                <Bar dataKey="LY EBITDA" name={String(year - 1)} fill={P.teal}        radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <SectionLabel text="Payroll % of Revenue — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: P.teal, label: `${year} Actual` }, { color: C.mid, label: String(year - 1), dashed: true }, { color: C.yellowDark, label: 'Budget', dashed: true }]}/>
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={fyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: C.mid }} width={36} axisLine={false} tickLine={false} domain={['auto','auto']}/>
                <Tooltip content={<DarkTooltip fmtVal={pctTip}/>}/>
                <Bar dataKey="payrollPct" name="Actual %" fill={P.teal} radius={[3,3,0,0]} maxBarSize={32}/>
                <Line type="monotone" dataKey="payrollLY"  name={String(year-1)} stroke={C.mid}        strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
                <Line type="monotone" dataKey="payrollBgt" name="Budget"          stroke={C.yellowDark} strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <SectionLabel text="Food Cost % of Food Revenue — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: P.pastelGreen, label: `${year} Actual` }, { color: C.mid, label: String(year - 1), dashed: true }, { color: C.yellowDark, label: 'Budget', dashed: true }]}/>
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={fyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: C.mid }} width={36} axisLine={false} tickLine={false} domain={['auto','auto']}/>
                <Tooltip content={<DarkTooltip fmtVal={pctTip}/>}/>
                <Bar dataKey="foodPct" name="Actual %" fill={P.pastelGreen} radius={[3,3,0,0]} maxBarSize={32}/>
                <Line type="monotone" dataKey="foodLY"  name={String(year-1)} stroke={C.mid}        strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
                <Line type="monotone" dataKey="foodBgt" name="Budget"          stroke={C.yellowDark} strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {!isSassys && (
            <>
              <SectionLabel text="Bev Cost % of Bev Revenue — Full Year"/>
              <div style={box}>
                <ChartLegend items={[{ color: P.pastelBlue, label: `${year} Actual` }, { color: C.mid, label: String(year - 1), dashed: true }, { color: C.yellowDark, label: 'Budget', dashed: true }]}/>
                <ResponsiveContainer width="100%" height={190}>
                  <ComposedChart data={fyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                    <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: C.mid }} width={36} axisLine={false} tickLine={false} domain={['auto','auto']}/>
                    <Tooltip content={<DarkTooltip fmtVal={pctTip}/>}/>
                    <Bar dataKey="bevPct" name="Actual %" fill={P.pastelBlue} radius={[3,3,0,0]} maxBarSize={32}/>
                    <Line type="monotone" dataKey="bevLY"  name={String(year-1)} stroke={C.mid}        strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
                    <Line type="monotone" dataKey="bevBgt" name="Budget"          stroke={C.yellowDark} strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls/>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Venues Tab ────────────────────────────────────────────────────────────────
// 3 venue cards side by side. Inside each: 4 stacked metric sub-cards.
function VenuesTab({ monthly, monthlyLY, weekly, weeklyLY, dailyRev, dailyRevLY, month, year, showYTD, isMobile }) {
  const [modalVenue, setModalVenue] = useState(null);

  // A month is complete only if it has fully passed
  const nowM = new Date().getMonth() + 1;
  const nowY = new Date().getFullYear();
  const isMonthComplete = (m) => year < nowY || (year === nowY && m < nowM);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: isMobile ? 8 : 14 }}>
      {VENUES.map(v => {
        const meta   = VENUE_META[v];
        const mRow   = (monthly[v]   || []).find(r => r.month === month);
        const mRowLY = (monthlyLY[v] || []).find(r => r.month === month);
        const closed = isMonthComplete(month);
        const isMTD  = !showYTD && !closed;

        // YTD: only completed months — revenue from daily, costs from monthly
        const ytdRows   = (monthly[v]   || []).filter(r => r.month <= month && isMonthComplete(r.month));
        const ytdRowsLY = (monthlyLY[v] || []).filter(r => r.month <= month && isMonthComplete(r.month));
        const sum = (rows, fn) => rows.reduce((s, r) => s + fn(r), 0);

        const ytdRev   = ytdRows.reduce((s, r) => s + (dailyMonthRev(dailyRev[v], r.month) || safe(r.actual_revenue)), 0);
        const ytdRevLY = ytdRowsLY.reduce((s, r) => s + (dailyMonthRev(dailyRevLY[v], r.month) || safe(r.actual_revenue)), 0);
        const ytdPay   = sum(ytdRows,   r => rowPayroll(r));
        const ytdPayLY = sum(ytdRowsLY, r => rowPayroll(r));
        const ytdEbi   = sum(ytdRows,   r => rowEBITDA(r));
        const ytdEbiLY = sum(ytdRowsLY, r => rowEBITDA(r));

        // MTD: sum daily revenue for current month
        const mtdRev   = dailyMonthRev(dailyRev[v],   month);
        const dayOfMth = latestDayOfMonth(dailyRev[v], month);
        const mtdRevLY = dayOfMth
          ? (dailyRevLY[v] || [])
              .filter(d => new Date(d.date).getMonth()+1 === month && new Date(d.date).getDate() <= dayOfMth && (d.total_revenue||0) > 0)
              .reduce((s,d) => s + (d.total_revenue||0), 0)
          : 0;

        // Closed month revenue from daily (fallback to results_monthly)
        const closedRev   = dailyMonthRev(dailyRev[v],   month) || safe(mRow?.actual_revenue);
        const closedRevLY = dailyMonthRev(dailyRevLY[v], month) || safe(mRowLY?.actual_revenue);

        const pay    = rowPayroll(mRow);
        const payLY  = rowPayroll(mRowLY);
        const ebi    = rowEBITDA(mRow);
        const ebiLY  = rowEBITDA(mRowLY);

        // MTD costs: pro-rate budget figures by days elapsed
        const daysInMth   = new Date(nowY, month, 0).getDate();
        const proRateFrac = dayOfMth ? Math.min(dayOfMth / daysInMth, 1) : 0;
        const budgetPayroll = mRow
          ? (safe(mRow.budget_kitchen_payroll) + safe(mRow.budget_foh_payroll)) * proRateFrac
          : null;
        const budgetEBITDA = mRow && budgetPayroll !== null
          ? (safe(mRow.budget_revenue) * proRateFrac)
            - (safe(mRow.budget_food_cost) + safe(mRow.budget_bev_cost)
            + safe(mRow.budget_kitchen_payroll) + safe(mRow.budget_foh_payroll)
            + safe(mRow.budget_group_costs) + safe(mRow.budget_other_opex)) * proRateFrac
          : null;

        const rev   = showYTD ? ytdRev  : (closed ? closedRev   : (mtdRev   > 0 ? mtdRev   : 0));
        const revLY = showYTD ? (ytdRevLY || null) : (closed ? (closedRevLY || null) : (mtdRevLY > 0 ? mtdRevLY : null));
        const payD  = showYTD ? ytdPay  : (closed ? pay  : (budgetPayroll !== null ? budgetPayroll : null));
        const payDLY = showYTD ? (ytdPayLY  || null) : (payLY > 0 ? payLY : null);
        const ebiD   = showYTD ? ytdEbi  : (closed ? ebi  : (budgetEBITDA !== null ? budgetEBITDA : null));
        const ebiDLY = showYTD ? (ytdEbiLY !== 0 ? ytdEbiLY : null) : (ebiLY !== 0 ? ebiLY : null);
        const exp    = rev  != null && payD  != null && ebiD  != null ? rev  - payD  - ebiD  : null;
        const expLY  = revLY != null && payDLY != null && ebiDLY != null ? revLY - payDLY - ebiDLY : null;

        const metrics = [
          { label: 'Revenue',  value: rev,  lyVal: revLY,  negGood: false },
          { label: 'Payroll',  value: payD, lyVal: payDLY, negGood: true  },
          { label: 'Expenses', value: exp,  lyVal: expLY,  negGood: true  },
          { label: 'EBITDA',   value: ebiD, lyVal: ebiDLY, negGood: false },
        ];

        return (
          <div key={v}
            onClick={() => setModalVenue(v)}
            style={{
              background: C.white,
              border: `1.5px solid ${C.border}`,
              borderRadius: isMobile ? 10 : 14,
              borderTop: `3px solid ${meta.color}`,
              padding: isMobile ? '10px 8px' : '16px 14px',
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'box-shadow 0.15s',
              display: 'flex', flexDirection: 'column', gap: isMobile ? 7 : 9,
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}
          >
            {/* Venue header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: isMobile ? 10 : 11, fontWeight: 800, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {meta.short}
              </span>
              <div style={{ display: 'flex', gap: 3 }}>
                {isMTD  && <span style={{ fontSize: 8, fontWeight: 700, color: C.amber,       background: C.amberLight,   padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase' }}>MTD</span>}
                {showYTD && <span style={{ fontSize: 8, fontWeight: 700, color: C.navy,        background: C.skyBlueLight, padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase' }}>YTD</span>}
              </div>
            </div>

            {/* 4 metric sub-cards stacked */}
            {metrics.map(({ label, value, lyVal, negGood }) => {
              const diff   = lyVal != null && lyVal !== 0 ? value - lyVal : null;
              const good   = diff == null ? null : (negGood ? diff <= 0 : diff >= 0);
              const subBg  = good === true ? C.greenLight : good === false ? C.redLight : '#f8fafc';
              const subBdr = good === true ? `${C.green}44` : good === false ? `${C.red}33` : C.border;
              const subCol = good === true ? C.green : good === false ? C.red : C.mid;

              return (
                <div key={label} style={{
                  background: C.cream,
                  borderRadius: isMobile ? 7 : 8,
                  border: `1px solid ${C.border}`,
                  padding: isMobile ? '8px 9px' : '10px 12px',
                }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: isMobile ? 3 : 4 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 900, color: C.charcoal, letterSpacing: '-0.01em', marginBottom: isMobile ? 5 : 7 }}>
                    {value != null ? fmt(value) : '—'}
                  </div>
                  <div style={{ background: subBg, border: `1px solid ${subBdr}`, borderRadius: 4, padding: isMobile ? '3px 6px' : '4px 8px' }}>
                    <div style={{ fontSize: 8, fontWeight: 700, color: C.mid, textTransform: 'uppercase', marginBottom: 1 }}>vs LY</div>
                    {diff != null
                      ? <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 800, color: subCol }}>
                          {diff >= 0 ? '+' : ''}{fmt(Math.round(diff))}
                        </div>
                      : <div style={{ fontSize: 9, color: C.border }}>—</div>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {modalVenue && (
        <VenueModal venue={modalVenue} month={month} year={year}
          monthly={monthly[modalVenue] || []} monthlyLY={monthlyLY[modalVenue] || []}
          weekly={weekly[modalVenue] || []} weeklyLY={weeklyLY[modalVenue] || []}
          dailyRev={dailyRev[modalVenue] || []} dailyRevLY={dailyRevLY[modalVenue] || []}
          showYTD={showYTD}
          onClose={() => setModalVenue(null)}
        />
      )}
    </div>
  );
}

// ── Group Tab ─────────────────────────────────────────────────────────────────
function GroupTab({ monthly, monthlyLY, dailyRev, dailyRevLY, month, year, showYTD, isMobile }) {

  // Aggregate a single month across all venues — revenue from daily first
  const groupMonth = (m, byVenue, dr) => {
    const rows = VENUES.map(v => (byVenue[v] || []).find(r => r.month === m));
    return {
      revenue:  VENUES.reduce((s, v) => s + (dailyMonthRev(dr[v], m) || safe((byVenue[v]||[]).find(r=>r.month===m)?.actual_revenue)), 0),
      payroll:  rows.reduce((s, r) => s + rowPayroll(r), 0),
      ebitda:   rows.reduce((s, r) => s + rowEBITDA(r), 0),
      hours:    rows.reduce((s, r) => s + safe(r?.kitchen_hours) + safe(r?.foh_hours), 0),
    };
  };

  // Aggregate YTD up to month
  const groupYTD = (byVenue, upToMonth, dr) =>
    Array.from({ length: upToMonth }, (_, i) => groupMonth(i + 1, byVenue, dr))
      .reduce((acc, m) => ({
        revenue: acc.revenue + m.revenue,
        payroll: acc.payroll + m.payroll,
        ebitda:  acc.ebitda  + m.ebitda,
        hours:   acc.hours   + m.hours,
      }), { revenue: 0, payroll: 0, ebitda: 0, hours: 0 });

  const cur   = showYTD ? groupYTD(monthly, month, dailyRev)   : groupMonth(month, monthly, dailyRev);
  const curLY = showYTD ? groupYTD(monthlyLY, month, dailyRevLY) : groupMonth(month, monthlyLY, dailyRevLY);

  const curExp   = cur.revenue   - cur.payroll   - cur.ebitda;
  const curExpLY = curLY.revenue - curLY.payroll - curLY.ebitda;

  // A month is complete only if it has fully passed (not current month of current year)
  const nowM = new Date().getMonth() + 1;
  const nowY = new Date().getFullYear();
  const isMonthComplete = (m) => year < nowY || (year === nowY && m < nowM);

  // Helper: revenue for a venue/month — daily first, fallback to monthly
  const venueMonthRev = (v, m, dr, mo) => dailyMonthRev(dr[v], m) || safe((mo[v]||[]).find(r=>r.month===m)?.actual_revenue);

  const venueRevs = VENUES.map(v => ({
    venue: v,
    rev: showYTD
      ? (monthly[v] || []).filter(r => r.month <= month && isMonthComplete(r.month))
          .reduce((s, r) => s + (venueMonthRev(v, r.month, dailyRev, monthly) || 0), 0)
      : venueMonthRev(v, month, dailyRev, monthly),
  }));
  const totalRev = venueRevs.reduce((s, x) => s + (x.rev||0), 0);

  // Full-year chart data
  const stackedYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    return {
      label,
      Jordaan:  complete ? venueMonthRev('Box Jordaan',  m, dailyRev, monthly) || null : null,
      Plantage: complete ? venueMonthRev('Box Plantage', m, dailyRev, monthly) || null : null,
      Sassys:   complete ? venueMonthRev('Sassys',       m, dailyRev, monthly) || null : null,
    };
  });

  const ebitdaYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    return {
      label,
      Jordaan:  complete && (monthly['Box Jordaan']  || []).find(r => r.month === m) ? rowEBITDA((monthly['Box Jordaan']  || []).find(r => r.month === m)) : null,
      Plantage: complete && (monthly['Box Plantage'] || []).find(r => r.month === m) ? rowEBITDA((monthly['Box Plantage'] || []).find(r => r.month === m)) : null,
      Sassys:   complete && (monthly['Sassys']       || []).find(r => r.month === m) ? rowEBITDA((monthly['Sassys']       || []).find(r => r.month === m)) : null,
    };
  });

  const brunchDinnerYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    const brunch = complete ? VENUES.reduce((s, v) => s + safe((monthly[v] || []).find(r => r.month === m)?.brunch_revenue), 0) : 0;
    const dinner = complete ? VENUES.reduce((s, v) => s + safe((monthly[v] || []).find(r => r.month === m)?.dinner_revenue), 0) : 0;
    return { label, Brunch: brunch || null, Dinner: dinner || null };
  });

  const coversYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    const cy = complete ? VENUES.reduce((s, v) => s + safe((monthly[v]   || []).find(r => r.month === m)?.actual_covers), 0) : 0;
    const ly = VENUES.reduce((s, v) => s + safe((monthlyLY[v] || []).find(r => r.month === m)?.actual_covers), 0);
    return { label, 'Covers': cy || null, 'Covers LY': ly || null };
  });

  const rpcYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    const cyRev    = complete ? VENUES.reduce((s, v) => s + (venueMonthRev(v, m, dailyRev, monthly)||0), 0) : 0;
    const lyRev    = VENUES.reduce((s, v) => s + (venueMonthRev(v, m, dailyRevLY, monthlyLY)||0), 0);
    const cyCovers = complete ? VENUES.reduce((s, v) => s + safe((monthly[v]   || []).find(r => r.month === m)?.actual_covers),  0) : 0;
    const lyCovers = VENUES.reduce((s, v) => s + safe((monthlyLY[v] || []).find(r => r.month === m)?.actual_covers),  0);
    return {
      label,
      'Rev/Cover':    cyCovers > 0 ? parseFloat((cyRev / cyCovers).toFixed(2)) : null,
      'Rev/Cover LY': lyCovers > 0 ? parseFloat((lyRev / lyCovers).toFixed(2)) : null,
    };
  });

  const hoursYear = MONTHS.map((label, i) => {
    const m = i + 1;
    const complete = isMonthComplete(m);
    const cy = complete ? VENUES.reduce((s, v) => {
      const r = (monthly[v]   || []).find(x => x.month === m);
      return s + safe(r?.kitchen_hours) + safe(r?.foh_hours);
    }, 0) : 0;
    const ly = VENUES.reduce((s, v) => {
      const r = (monthlyLY[v] || []).find(x => x.month === m);
      return s + safe(r?.kitchen_hours) + safe(r?.foh_hours);
    }, 0);
    return { label, 'Hours': cy || null, 'Hours LY': ly || null };
  });

  const box = { background: C.cream, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` };

  // Period label for section headers
  const periodLabel = showYTD ? `Jan–${MONTHS[month - 1]} YTD` : MONTHS[month - 1];

  // Summary KPI strip
  const kpis = [
    { label: 'Revenue',   value: cur.revenue,   lyValue: curLY.revenue,   color: P.revenueAccent, neg: false },
    { label: 'Payroll',   value: cur.payroll,   lyValue: curLY.payroll,   color: P.payrollAccent, neg: true  },
    { label: 'Expenses',  value: curExp,         lyValue: curExpLY,        color: P.expensesAccent,neg: true  },
    { label: 'EBITDA',    value: cur.ebitda,    lyValue: curLY.ebitda,    color: P.ebitdaAccent,  neg: false },
  ];

  return (
    <div>
      {/* KPI summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : 4}, 1fr)`, gap: isMobile ? 7 : 10, marginBottom: 20 }}>
        {kpis.map(k => {
          const diff = curLY[k.label.toLowerCase()] !== undefined
            ? k.value - k.lyValue
            : k.label === 'Expenses' ? curExp - curExpLY : null;
          const validDiff = k.lyValue && k.lyValue !== 0 ? k.value - k.lyValue : null;
          const good = validDiff == null ? null : (k.neg ? validDiff <= 0 : validDiff >= 0);
          const bg   = good === true ? C.greenLight : good === false ? C.redLight : '#f8fafc';
          const col  = good === true ? C.green : good === false ? C.red : C.charcoal;
          return (
            <div key={k.label} style={{ background: bg, borderRadius: 10, padding: isMobile ? '10px 10px' : '12px 14px', border: `1px solid ${good === true ? C.green + '44' : good === false ? C.red + '33' : C.border}`, borderLeft: `3px solid ${k.color}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k.label} · {periodLabel}</div>
              <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 900, color: col, letterSpacing: '-0.02em', marginBottom: 3 }}>{fmtK2(k.value)}</div>
              {validDiff != null
                ? <div style={{ fontSize: 10, fontWeight: 700, color: col }}>{validDiff >= 0 ? '+' : ''}{fmtK2(Math.round(validDiff))} vs LY</div>
                : <div style={{ fontSize: 10, color: C.border }}>No LY</div>}
            </div>
          );
        })}
      </div>

      {/* Revenue contribution this period */}
      {totalRev > 0 && (
        <>
          <SectionLabel text={`Revenue Contribution — ${periodLabel}`}/>
          <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 4 }}>
            <div style={{ display: 'flex', height: 20, borderRadius: 6, overflow: 'hidden', gap: 2, marginBottom: 10 }}>
              {venueRevs.map(({ venue, rev }) => (
                <div key={venue} style={{ flex: rev, background: venue === 'Box Jordaan' ? '#4A7B8C' : venue === 'Box Plantage' ? '#A8D5B5' : '#F0C87A' }} title={`${VENUE_META[venue].short}: ${fmt(rev)}`}/>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {venueRevs.map(({ venue, rev }) => (
                <div key={venue} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: venue === 'Box Jordaan' ? '#4A7B8C' : venue === 'Box Plantage' ? '#A8D5B5' : '#F0C87A' }}/>
                  <span style={{ fontSize: 12, color: C.mid }}>{VENUE_META[venue].short} <strong style={{ color: C.charcoal }}>{fmt(rev)}</strong> {totalRev > 0 ? `(${((rev / totalRev) * 100).toFixed(0)}%)` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <SectionLabel text="Revenue by Venue — Full Year"/>
      <div style={box}>
        <ChartLegend items={[{ color: '#4A7B8C', label: 'Jordaan' }, { color: '#A8D5B5', label: 'Plantage' }, { color: '#F0C87A', label: 'Sassys' }]}/>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={stackedYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
            <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
            <Bar dataKey="Jordaan"  name="Jordaan"  stackId="a" fill="#4A7B8C"/>
            <Bar dataKey="Plantage" name="Plantage" stackId="a" fill="#A8D5B5"/>
            <Bar dataKey="Sassys"   name="Sassys"   stackId="a" fill="#F0C87A" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <SectionLabel text="EBITDA by Venue — Full Year"/>
      <div style={box}>
        <ChartLegend items={[{ color: '#4A7B8C', label: 'Jordaan' }, { color: '#A8D5B5', label: 'Plantage' }, { color: '#F0C87A', label: 'Sassys' }]}/>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={ebitdaYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
            <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
            <ReferenceLine y={0} stroke={C.mid} strokeOpacity={0.4}/>
            <Bar dataKey="Jordaan"  name="Jordaan"  stackId="a" fill="#4A7B8C"/>
            <Bar dataKey="Plantage" name="Plantage" stackId="a" fill="#A8D5B5"/>
            <Bar dataKey="Sassys"   name="Sassys"   stackId="a" fill="#F0C87A" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Hours — Full Year */}
      {hoursYear.some(d => d['Hours']) && (
        <>
          <SectionLabel text="Group Hours — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: '#E4AC49', label: String(year) }, { color: '#C4860A', label: String(year - 1), dashed: true }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={hoursYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: C.mid }} width={40} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={v => v + 'h'}/>}/>
                <Line type="monotone" dataKey="Hours"    name={String(year)}     stroke="#E4AC49" strokeWidth={2.5} dot={false} connectNulls/>
                <Line type="monotone" dataKey="Hours LY" name={String(year - 1)} stroke="#C4860A" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {brunchDinnerYear.some(d => d.Brunch || d.Dinner) && (
        <>
          <SectionLabel text="Brunch vs Dinner — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: '#F0C87A', label: 'Brunch' }, { color: '#4A7B8C', label: 'Dinner' }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={brunchDinnerYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
                <Bar dataKey="Brunch" name="Brunch" stackId="a" fill="#F0C87A"/>
                <Bar dataKey="Dinner" name="Dinner" stackId="a" fill="#4A7B8C" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {coversYear.some(d => d['Covers'] || d['Covers LY']) && (
        <>
          <SectionLabel text="Group Covers — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: '#6366f1', label: String(year) }, { color: C.mid, label: String(year - 1), dashed: true }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={coversYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 10, fill: C.mid }} width={40} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={v => Number(v).toLocaleString()}/>}/>
                <Line type="monotone" dataKey="Covers"    name={String(year)}     stroke="#6366f1" strokeWidth={2.5} dot={false} connectNulls/>
                <Line type="monotone" dataKey="Covers LY" name={String(year - 1)} stroke={C.mid}   strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {rpcYear.some(d => d['Rev/Cover'] || d['Rev/Cover LY']) && (
        <>
          <SectionLabel text="Revenue per Cover — Full Year"/>
          <div style={box}>
            <ChartLegend items={[{ color: C.green, label: String(year) }, { color: C.mid, label: String(year - 1), dashed: true }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={rpcYear} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`€${Math.round(v)}`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={v=>`€${Math.round(v)}`}/>}/>
                <Line type="monotone" dataKey="Rev/Cover"    name={String(year)}     stroke={C.green} strokeWidth={2.5} dot={false} connectNulls/>
                <Line type="monotone" dataKey="Rev/Cover LY" name={String(year - 1)} stroke={C.mid}   strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ── Costs Tab — Lekkermate Shared Costs ───────────────────────────────────────
function CostsTab({ lmData, year, isMobile }) {
  const box = { background: C.cream, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}`, marginBottom: 16 };

  const groupChartData = MONTHS.map((m, i) => {
    const row = lmData.find(r => r.month === i + 1);
    const j = row?.alloc_jordaan_total  || 0;
    const p = row?.alloc_plantage_total || 0;
    const s = row?.alloc_sassys_total   || 0;
    const food    = (row?.alloc_jordaan_food    || 0) + (row?.alloc_plantage_food    || 0) + (row?.alloc_sassys_food    || 0);
    const payroll = (row?.alloc_jordaan_payroll || 0) + (row?.alloc_plantage_payroll || 0) + (row?.alloc_sassys_payroll || 0);
    const other   = (row?.alloc_jordaan_other   || 0) + (row?.alloc_plantage_other   || 0) + (row?.alloc_sassys_other   || 0);
    const mgmt    = (row?.alloc_jordaan_mgmt    || 0) + (row?.alloc_plantage_mgmt    || 0) + (row?.alloc_sassys_mgmt    || 0);
    return {
      month: m,
      Jordaan: j || null, Plantage: p || null, Sassys: s || null,
      'Food Cost': food || null, Payroll: payroll || null, 'Other Costs': (other + mgmt) || null,
    };
  });

  const hasData = lmData.length > 0;

  return (
    <div>
      {/* Header banner */}
      <div style={{ background: C.skyBlueLight, border: `1px solid ${C.skyBlue}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.skyText, flexShrink: 0 }}/>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.skyText }}>Lekker Mate B.V. — Shared Costs</div>
        </div>
      </div>

      {!hasData ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.mid, fontSize: 13 }}>No shared costs data for {year}.</div>
      ) : (
        <>
          {/* Chart 1: by venue */}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Total Allocation by Venue</div>
          <div style={box}>
            <ChartLegend items={[{ color: '#4A7B8C', label: 'Jordaan' }, { color: '#A8D5B5', label: 'Plantage' }, { color: '#F0C87A', label: 'Sassys' }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={groupChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
                <Bar dataKey="Jordaan"  name="Jordaan"  stackId="a" fill="#4A7B8C"/>
                <Bar dataKey="Plantage" name="Plantage" stackId="a" fill="#A8D5B5"/>
                <Bar dataKey="Sassys"   name="Sassys"   stackId="a" fill="#F0C87A" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 2: by component */}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Breakdown by Component</div>
          <div style={box}>
            <ChartLegend items={[{ color: '#7EA594', label: 'Food Cost' }, { color: '#F2B8CB', label: 'Payroll' }, { color: '#BCD9E5', label: 'Other / Mgmt' }]}/>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={groupChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: C.mid }} width={46} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip fmtVal={fmt}/>}/>
                <Bar dataKey="Food Cost"   name="Food Cost"    stackId="a" fill="#7EA594"/>
                <Bar dataKey="Payroll"     name="Payroll"      stackId="a" fill="#F2B8CB"/>
                <Bar dataKey="Other Costs" name="Other / Mgmt" stackId="a" fill="#BCD9E5" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly table */}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Monthly Allocation Detail</div>
          <div style={{ background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '520px' : undefined }}>
                <thead>
                  <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                    {['Month','Payroll','Food','Other','Total','Jordaan','Plantage','Sassys'].map((h, i) => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: i === 0 ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: C.mid, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map((m, i) => {
                    const row = lmData.find(r => r.month === i + 1);
                    const j = row?.alloc_jordaan_total  || 0;
                    const p = row?.alloc_plantage_total || 0;
                    const s = row?.alloc_sassys_total   || 0;
                    const grand   = j + p + s;
                    const food    = (row?.alloc_jordaan_food    || 0) + (row?.alloc_plantage_food    || 0) + (row?.alloc_sassys_food    || 0);
                    const payroll = (row?.alloc_jordaan_payroll || 0) + (row?.alloc_plantage_payroll || 0) + (row?.alloc_sassys_payroll || 0);
                    const other   = (row?.alloc_jordaan_other   || 0) + (row?.alloc_plantage_other   || 0) + (row?.alloc_sassys_other   || 0)
                                  + (row?.alloc_jordaan_mgmt    || 0) + (row?.alloc_plantage_mgmt    || 0) + (row?.alloc_sassys_mgmt    || 0);
                    const isCurrent = false; // costs tab shows full year, no current-month highlight
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.cream }}>
                        <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 400 }}>{m}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: C.charcoal, fontWeight: 600 }}>{payroll ? fmt(payroll) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: C.charcoal, fontWeight: 600 }}>{food ? fmt(food) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: C.charcoal, fontWeight: 600 }}>{other ? fmt(other) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 13, fontWeight: 800, color: C.charcoal }}>{grand ? fmt(grand) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: '#4A7B8C', fontWeight: 600 }}>{j ? fmt(j) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: '#5a9e6f', fontWeight: 600 }}>{p ? fmt(p) : '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: '#b8860b', fontWeight: 600 }}>{s ? fmt(s) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GroupResults({ currentUser }) {
  const isMobile = useIsMobile();
  const [tab,      setTab]      = useState('group');
  const [month,    setMonth]    = useState(new Date().getMonth() + 1);
  const [year,     setYear]     = useState(CURRENT_YEAR);
  const [showYTD,  setShowYTD]  = useState(false);
  const [loading,  setLoading]  = useState(true);

  const [monthly,   setMonthly]   = useState({});
  const [monthlyLY, setMonthlyLY] = useState({});
  const [weekly,    setWeekly]    = useState({});
  const [weeklyLY,  setWeeklyLY]  = useState({});
  const [lmData,    setLmData]    = useState([]);

  const [dailyRev,   setDailyRev]   = useState({});
  const [dailyRevLY, setDailyRevLY] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all([
      ...VENUES.flatMap(v => [
        supabase.from('results_monthly').select('*').eq('venue', v).eq('year', year).order('month'),
        supabase.from('results_monthly').select('*').eq('venue', v).eq('year', year - 1).order('month'),
        supabase.from('results').select('*').eq('venue', v).eq('year', year).order('week'),
        supabase.from('results').select('*').eq('venue', v).eq('year', year - 1).order('week'),
        supabase.from('results_daily').select('date,total_revenue,venue').eq('venue', v).eq('year', year),
        supabase.from('results_daily').select('date,total_revenue,venue').eq('venue', v).eq('year', year - 1),
      ]),
      supabase.from('lekkermate_monthly').select('*').eq('year', year).order('month'),
    ]);

    const m = {}, mLY = {}, w = {}, wLY = {}, dr = {}, drLY = {};
    VENUES.forEach((v, i) => {
      m[v]    = results[i * 6 + 0].data || [];
      mLY[v]  = results[i * 6 + 1].data || [];
      w[v]    = results[i * 6 + 2].data || [];
      wLY[v]  = results[i * 6 + 3].data || [];
      dr[v]   = results[i * 6 + 4].data || [];
      drLY[v] = results[i * 6 + 5].data || [];
    });
    setMonthly(m); setMonthlyLY(mLY); setWeekly(w); setWeeklyLY(wLY);
    setDailyRev(dr); setDailyRevLY(drLY);
    setLmData(results[VENUES.length * 6].data || []);
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const now         = new Date();
  const isAtCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  const prevMonth   = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth   = () => {
    if (isAtCurrent) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1);
  };

  return (
    <div style={{ padding: isMobile ? '16px 14px' : '24px', maxWidth: 1300, fontFamily: 'inherit' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.charcoal, letterSpacing: '-0.01em' }}>Group Overview</h2>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: C.mid }}>Admin · All venues · {year}</p>
      </div>

      <ControlsBar
        tab={tab} setTab={setTab}
        month={month} year={year}
        onPrev={prevMonth} onNext={nextMonth} disableNext={isAtCurrent}
        setYear={setYear} showYTD={showYTD} setShowYTD={setShowYTD}
        isMobile={isMobile}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.mid, fontSize: 14 }}>Loading group data…</div>
      ) : (
        <>
          {tab === 'venues' && (
            <VenuesTab
              monthly={monthly} monthlyLY={monthlyLY}
              weekly={weekly} weeklyLY={weeklyLY}
              dailyRev={dailyRev} dailyRevLY={dailyRevLY}
              month={month} year={year} showYTD={showYTD} isMobile={isMobile}
            />
          )}
          {tab === 'group' && (
            <GroupTab
              monthly={monthly} monthlyLY={monthlyLY}
              dailyRev={dailyRev} dailyRevLY={dailyRevLY}
              month={month} year={year} showYTD={showYTD} isMobile={isMobile}
            />
          )}
          {tab === 'costs' && (
            <CostsTab lmData={lmData} year={year} isMobile={isMobile}/>
          )}
        </>
      )}
    </div>
  );
}